import React, { useState, useEffect, memo, useCallback, useRef, useMemo } from 'react';
import {
    TextField,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Fab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Checkbox,
    FormControlLabel,
    LinearProgress,
    Card,
    CardMedia,
    CardContent,
    Divider,
    Backdrop,
    Menu,
    MenuItem,
    ListItemIcon,
    InputAdornment,
    Skeleton,
    Collapse,
    Chip,
    Select,
    InputLabel,
    FormControl,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import theme from '../../../theme';
import Breadcrumbs from '../../global/Components/Breadcrumbs';
import floorplanService from './services/floorplanService';
import { fetchProjectById } from '../ProjectDashboard/services/projectDashboardService';
import { isExternalOwnerConsultantRole } from '../../../utils/roles';
import { formatDate } from '../../utils/dateUtils';
import {
    convertPdfToImages,
    isPdfFile,
    isImageFile,
    dataUrlToFile,
} from '../../utils/pdfToImageConverter';

// Constants
const PDF_CONVERSION_CONFIG = {
    quality: 0.9,
    scale: 3.0,
};

const UI_CONSTANTS = {
    pagePreviewHeight: 200,
    maxPageReviewHeight: 400,
    autoScrollDelay: 100,
    floorplanThumbnailHeight: 180,
};

const UPLOAD_PHASES = {
    FILE_SELECT: 'file-select',
    PAGE_REVIEW: 'page-review',
    UPLOADING: 'uploading',
};

const ImageContainer = styled(Box)(() => ({
    padding: 0,
    backgroundColor: 'transparent',
    overflow: 'visible',
    cursor: 'pointer',
    position: 'relative',
    transition: 'opacity 0.2s',
    '& .cardAction': {
        opacity: 0,
        transform: 'scale(0.95)',
        transition: 'opacity 0.2s ease, transform 0.2s ease',
    },
    '&:hover .cardAction, &:focus-within .cardAction': {
        opacity: 1,
        transform: 'scale(1)',
    },
    // Ensure actions are visible on touch devices (no hover available)
    '@media (hover: none)': {
        '& .cardAction': {
            opacity: 1,
            transform: 'scale(1)',
        },
    },
    '&:hover': {
        opacity: 0.85,
    },
}));

const SitePlanImage = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
});

const ThumbnailWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    height: UI_CONSTANTS.floorplanThumbnailHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[50],
    overflow: 'hidden',
    borderRadius: '8px',
    border: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    '.MuiBox-root:hover &': {
        borderColor: theme.palette.grey[300],
        boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
    },
}));

// Memoized Floorplan Card Component to prevent unnecessary re-renders
const FloorplanCard = memo(
    ({
        plan,
        onClick,
        onDelete,
        onDuplicate,
        onRename,
        onChangeGroup,
        isVisible,
        canWrite,
        showGrouping,
    }) => {
        const [imageError, setImageError] = useState(false);
        const [menuAnchorEl, setMenuAnchorEl] = useState(null);

        // Memoize the click handler to prevent prop changes
        const handleClick = useCallback(() => onClick(plan), [onClick, plan]);

        const handleImageError = useCallback(
            (e) => {
                console.warn(`Failed to load image for floorplan: ${plan.fileName}`, plan.imageUrl);
                setImageError(true);
                e.target.onerror = null; // Prevent infinite loop
            },
            [plan.fileName, plan.imageUrl]
        );

        const handleMenuOpen = useCallback((e) => {
            e.stopPropagation();
            setMenuAnchorEl(e.currentTarget);
        }, []);

        const handleMenuClose = useCallback(() => setMenuAnchorEl(null), []);

        const handleDelete = useCallback(() => {
            handleMenuClose();
            onDelete && onDelete(plan);
        }, [handleMenuClose, onDelete, plan]);

        const handleDuplicate = useCallback(() => {
            handleMenuClose();
            onDuplicate && onDuplicate(plan);
        }, [handleMenuClose, onDuplicate, plan]);

        const handleRename = useCallback(() => {
            handleMenuClose();
            onRename && onRename(plan);
        }, [handleMenuClose, onRename, plan]);

        const handleChangeGroup = useCallback(() => {
            handleMenuClose();
            onChangeGroup && onChangeGroup(plan);
        }, [handleMenuClose, onChangeGroup, plan]);

        return (
            <Grid
                size={{ xs: 12, sm: 6, md: 4 }}
                sx={{
                    display: isVisible ? 'block' : 'none', // Hide instead of unmounting
                }}
            >
                <ImageContainer onClick={handleClick} sx={{ position: 'relative' }}>
                    {canWrite && (
                        <>
                            <IconButton
                                className="cardAction"
                                size="small"
                                aria-label="open actions"
                                onClick={handleMenuOpen}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                                    zIndex: 2,
                                }}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                            <Menu
                                anchorEl={menuAnchorEl}
                                open={Boolean(menuAnchorEl)}
                                onClose={handleMenuClose}
                                onClick={(e) => e.stopPropagation()}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <MenuItem onClick={handleRename}>
                                    <ListItemIcon>
                                        <DriveFileRenameOutlineIcon fontSize="small" />
                                    </ListItemIcon>
                                    Rename
                                </MenuItem>
                                <MenuItem onClick={handleDuplicate}>
                                    <ListItemIcon>
                                        <ContentCopyIcon fontSize="small" />
                                    </ListItemIcon>
                                    Duplicate
                                </MenuItem>
                                {showGrouping && (
                                    <MenuItem onClick={handleChangeGroup}>
                                        <ListItemIcon>
                                            <DriveFileMoveIcon fontSize="small" />
                                        </ListItemIcon>
                                        Change Building
                                    </MenuItem>
                                )}
                                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                                    <ListItemIcon sx={{ color: 'error.main' }}>
                                        <DeleteIcon fontSize="small" />
                                    </ListItemIcon>
                                    Delete
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                    {imageError ? (
                        <Box
                            sx={{
                                height: UI_CONSTANTS.floorplanThumbnailHeight,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'grey.100',
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: 'grey.200',
                            }}
                        >
                            <Typography variant="body2" color="textSecondary" align="center">
                                Image not available
                                <br />
                                <small>{plan.fileName}</small>
                            </Typography>
                        </Box>
                    ) : (
                        <ThumbnailWrapper>
                            <SitePlanImage
                                src={plan.imageUrl}
                                alt={plan.fileName}
                                loading="lazy"
                                onError={handleImageError}
                            />
                        </ThumbnailWrapper>
                    )}
                    <Box sx={{ pt: 1, pb: 2, px: 0.5 }}>
                        <Typography
                            variant="body2"
                            align="left"
                            sx={{
                                fontWeight: 500,
                                fontSize: '0.85rem',
                                color: 'text.primary',
                                lineHeight: 1.2,
                                transition: 'color 0.2s',
                                '.MuiBox-root:hover &': {
                                    color: theme.palette.primary.main,
                                },
                            }}
                        >
                            {plan.fileName}
                        </Typography>
                    </Box>
                </ImageContainer>
            </Grid>
        );
    },
    (prevProps, nextProps) => {
        // Custom comparison function to ensure we only re-render when necessary
        return (
            prevProps.plan.id === nextProps.plan.id &&
            prevProps.plan.fileName === nextProps.plan.fileName &&
            prevProps.plan.groupName === nextProps.plan.groupName &&
            prevProps.plan.imageUrl === nextProps.plan.imageUrl &&
            prevProps.onClick === nextProps.onClick &&
            prevProps.onDelete === nextProps.onDelete &&
            prevProps.onDuplicate === nextProps.onDuplicate &&
            prevProps.onRename === nextProps.onRename &&
            prevProps.onChangeGroup === nextProps.onChangeGroup &&
            prevProps.isVisible === nextProps.isVisible &&
            prevProps.canWrite === nextProps.canWrite &&
            prevProps.showGrouping === nextProps.showGrouping
        );
    }
);

FloorplanCard.displayName = 'FloorplanCard';

// PDF Page Review Component
const PdfPageReview = memo(
    ({
        convertedPages,
        selectedPages,
        onPageSelectionChange,
        onSelectAllPages,
        onPageNameChange,
        onRemovePage,
    }) => {
        return (
            <>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Review and Select Pages ({convertedPages.length} pages found)
                    </Typography>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedPages.length === convertedPages.length}
                                indeterminate={
                                    selectedPages.length > 0 &&
                                    selectedPages.length < convertedPages.length
                                }
                                onChange={(e) => onSelectAllPages(e.target.checked)}
                            />
                        }
                        label="Select All Pages"
                        sx={{ mb: 2 }}
                    />

                    <Grid
                        container
                        spacing={2}
                        sx={{ maxHeight: UI_CONSTANTS.maxPageReviewHeight, overflowY: 'auto' }}
                    >
                        {convertedPages.map((page) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={page.pageNumber}>
                                <Card sx={{ position: 'relative' }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <CardMedia
                                            component="img"
                                            height={UI_CONSTANTS.pagePreviewHeight}
                                            image={page.imageDataUrl}
                                            alt={`Page ${page.pageNumber}`}
                                            sx={{
                                                objectFit: 'contain',
                                                backgroundColor: 'grey.100',
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                left: 8,
                                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                color: 'white',
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1,
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            Page {page.pageNumber}
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={() => onRemovePage(page.pageNumber)}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                                },
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <CardContent>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selectedPages.includes(
                                                        page.pageNumber
                                                    )}
                                                    onChange={(e) =>
                                                        onPageSelectionChange(
                                                            page.pageNumber,
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="Include this page"
                                        />
                                        <TextField
                                            fullWidth
                                            label="Floorplan Name"
                                            value={page.fileName}
                                            onChange={(e) =>
                                                onPageNameChange(page.pageNumber, e.target.value)
                                            }
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{ display: 'block', mt: 1 }}
                                        >
                                            Size: ~{page.fileSize} KB
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </>
        );
    }
);

PdfPageReview.displayName = 'PdfPageReview';

// Custom hook for auto-scroll functionality for the upload page review section
const useAutoScroll = (trigger, targetRef) => {
    useEffect(() => {
        if (trigger && targetRef.current) {
            const timeoutId = setTimeout(() => {
                if (targetRef.current) {
                    targetRef.current.scrollTo({
                        top: targetRef.current.scrollHeight,
                        behavior: 'smooth',
                    });
                }
            }, UI_CONSTANTS.autoScrollDelay);

            return () => clearTimeout(timeoutId);
        }
    }, [trigger, targetRef]);
};

// Custom hook for PDF conversion
const usePdfConversion = () => {
    const [isConverting, setIsConverting] = useState(false);
    const [conversionProgress, setConversionProgress] = useState(0);
    const [convertedPages, setConvertedPages] = useState([]);

    const convertPdf = useCallback(async (pdfFile) => {
        setIsConverting(true);
        setConversionProgress(0);

        try {
            const pages = await convertPdfToImages(pdfFile, {
                ...PDF_CONVERSION_CONFIG,
                onProgress: (current, total) => {
                    setConversionProgress((current / total) * 100);
                },
            });

            setConvertedPages(pages);
            return pages;
        } catch (err) {
            console.error('PDF conversion failed:', err);
            throw new Error(err.message || 'Failed to convert PDF. Please try again.');
        } finally {
            setIsConverting(false);
        }
    }, []);

    const resetConversion = useCallback(() => {
        setIsConverting(false);
        setConversionProgress(0);
        setConvertedPages([]);
    }, []);

    return {
        isConverting,
        conversionProgress,
        convertedPages,
        setConvertedPages,
        convertPdf,
        resetConversion,
    };
};

// Custom hook for batch upload
const useBatchUpload = () => {
    const [uploadResults, setUploadResults] = useState([]);

    const batchUpload = useCallback(async (selectedPages, convertedPages, projectId, groupName) => {
        const results = [];

        for (const pageNumber of selectedPages) {
            const page = convertedPages.find((p) => p.pageNumber === pageNumber);
            if (!page) continue;

            try {
                const imageFile = dataUrlToFile(page.imageDataUrl, page.fileName);
                const floorplanData = {
                    projectId,
                    name: page.fileName,
                    groupName: groupName || null,
                };

                const result = await floorplanService.createFloorplan(floorplanData, imageFile);
                results.push({ success: true, page: pageNumber, result });
                console.log(`Page ${pageNumber} uploaded successfully:`, result);
            } catch (err) {
                console.error(`Error uploading page ${pageNumber}:`, err);
                results.push({ success: false, page: pageNumber, error: err.message });
            }
        }

        setUploadResults(results);
        return results;
    }, []);

    const resetUploadResults = useCallback(() => {
        setUploadResults([]);
    }, []);

    return {
        uploadResults,
        batchUpload,
        resetUploadResults,
    };
};

// Upload Modal Component
function FloorplanUploadModal({
    open,
    onClose,
    onUpload,
    projectId,
    groupingEnabled,
    buildingNames,
}) {
    // Basic state
    const [name, setName] = useState('');
    const [groupName, setGroupName] = useState(null);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [uploadPhase, setUploadPhase] = useState(UPLOAD_PHASES.FILE_SELECT);
    const [selectedPages, setSelectedPages] = useState([]);

    // Refs
    const dialogContentRef = useRef(null);

    // Custom hooks
    const {
        isConverting,
        conversionProgress,
        convertedPages,
        setConvertedPages,
        convertPdf,
        resetConversion,
    } = usePdfConversion();

    const { uploadResults, batchUpload, resetUploadResults } = useBatchUpload();

    // Auto-scroll when entering page review phase
    useAutoScroll(uploadPhase === UPLOAD_PHASES.PAGE_REVIEW, dialogContentRef);

    // File handling
    const handleFileSelect = useCallback(
        async (selectedFile) => {
            if (!selectedFile) return;

            setError('');
            setFile(selectedFile);

            if (isImageFile(selectedFile)) {
                setUploadPhase(UPLOAD_PHASES.FILE_SELECT);
                if (!name) {
                    const fileNameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '');
                    setName(fileNameWithoutExt);
                }
            } else if (isPdfFile(selectedFile)) {
                try {
                    const pages = await convertPdf(selectedFile);
                    setSelectedPages(pages.map((page) => page.pageNumber));
                    setUploadPhase(UPLOAD_PHASES.PAGE_REVIEW);
                } catch (err) {
                    setError(err.message);
                }
            } else {
                setError('Please select a valid image file (JPG, PNG, GIF) or PDF file');
            }
        },
        [convertPdf, name]
    );

    // Drag and drop handlers
    const handleDrop = useCallback(
        (e) => {
            e.preventDefault();
            setDragOver(false);
            const droppedFile = e.dataTransfer.files[0];
            handleFileSelect(droppedFile);
        },
        [handleFileSelect]
    );

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragOver(false);
    }, []);

    // Upload handlers
    const handleSingleImageUpload = useCallback(async () => {
        if (!name.trim()) {
            setError('Floorplan name is required');
            return;
        }
        if (!file) {
            setError('Please select an image file');
            return;
        }

        setUploading(true);
        setError('');

        try {
            const floorplanData = {
                projectId,
                name: name.trim(),
                groupName: groupName || null,
            };

            const result = await floorplanService.createFloorplan(floorplanData, file);

            onUpload(result);
            handleClose();
        } catch (err) {
            console.error('Error creating floorplan:', err);
            setError(err.message || 'Failed to upload floorplan');
        } finally {
            setUploading(false);
        }
    }, [name, file, projectId, groupName, onUpload]);

    const handleBatchPdfUpload = useCallback(async () => {
        if (selectedPages.length === 0) {
            setError('Please select at least one page to upload');
            return;
        }

        setUploading(true);
        setUploadPhase(UPLOAD_PHASES.UPLOADING);
        setError('');

        try {
            const results = await batchUpload(selectedPages, convertedPages, projectId, groupName);

            const successCount = results.filter((r) => r.success).length;
            if (successCount === results.length) {
                onUpload(); // Refresh the list
                handleClose();
            } else {
                const failedCount = results.length - successCount;
                setError(
                    `Upload completed: ${successCount} successful, ${failedCount} failed. See details below.`
                );
                setUploading(false);
            }
        } catch (err) {
            console.error('Batch upload failed:', err);
            setError(err.message || 'Failed to upload pages');
            setUploading(false);
        }
    }, [selectedPages, convertedPages, projectId, batchUpload, onUpload]);

    const handleSubmit = useCallback(async () => {
        if (uploadPhase === UPLOAD_PHASES.FILE_SELECT) {
            await handleSingleImageUpload();
        } else if (uploadPhase === UPLOAD_PHASES.PAGE_REVIEW) {
            await handleBatchPdfUpload();
        }
    }, [uploadPhase, handleSingleImageUpload, handleBatchPdfUpload]);

    // Page management
    const handlePageSelectionChange = useCallback((pageNumber, checked) => {
        setSelectedPages((prev) =>
            checked ? [...prev, pageNumber] : prev.filter((p) => p !== pageNumber)
        );
    }, []);

    const handleSelectAllPages = useCallback(
        (checked) => {
            setSelectedPages(checked ? convertedPages.map((page) => page.pageNumber) : []);
        },
        [convertedPages]
    );

    const handlePageNameChange = useCallback(
        (pageNumber, newName) => {
            setConvertedPages((prev) =>
                prev.map((page) =>
                    page.pageNumber === pageNumber ? { ...page, fileName: newName } : page
                )
            );
        },
        [setConvertedPages]
    );

    const handleRemovePage = useCallback(
        (pageNumber) => {
            setConvertedPages((prev) => prev.filter((page) => page.pageNumber !== pageNumber));
            setSelectedPages((prev) => prev.filter((p) => p !== pageNumber));
        },
        [setConvertedPages]
    );

    // Modal close
    const handleClose = useCallback(() => {
        setName('');
        setGroupName(null);
        setFile(null);
        setError('');
        setUploading(false);
        setDragOver(false);
        setSelectedPages([]);
        setUploadPhase(UPLOAD_PHASES.FILE_SELECT);
        resetConversion();
        resetUploadResults();
        onClose();
    }, [resetConversion, resetUploadResults, onClose]);

    // UI helpers
    const isFileSelectPhase = uploadPhase === UPLOAD_PHASES.FILE_SELECT;
    const isPageReviewPhase = uploadPhase === UPLOAD_PHASES.PAGE_REVIEW;
    const canSubmitSingleImage =
        isFileSelectPhase &&
        !uploading &&
        !isConverting &&
        file &&
        (!isImageFile(file) || name.trim());
    const canSubmitBatch = isPageReviewPhase && !uploading && selectedPages.length > 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Upload New Floorplan
                <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent ref={dialogContentRef}>
                <Box sx={{ mt: 2 }}>
                    {/* Name Field - Only show for single image uploads */}
                    {isFileSelectPhase && (
                        <TextField
                            fullWidth
                            label="Floorplan Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            margin="normal"
                            required={isImageFile(file)}
                        />
                    )}

                    {/* Building assignment - Only show when grouping is enabled */}
                    {groupingEnabled && buildingNames?.length > 0 && (
                        <FormControl fullWidth margin="normal" size="small">
                            <InputLabel>Building (Optional)</InputLabel>
                            <Select
                                value={groupName || ''}
                                onChange={(e) => setGroupName(e.target.value || null)}
                                label="Building (Optional)"
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {buildingNames.map((name) => (
                                    <MenuItem key={name} value={name}>
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* File Upload Area */}
                    <Box
                        component="label"
                        htmlFor="file-input"
                        sx={{
                            mt: 3,
                            border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.grey[300]}`,
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            backgroundColor: dragOver
                                ? theme.palette.primary.light + '10'
                                : 'transparent',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:focus-within': {
                                outline: '2px solid #1652F0',
                                outlineOffset: '2px',
                            },
                        }}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                    >
                        <input
                            id="file-input"
                            type="file"
                            aria-label="Upload a siteplan image or PDF"
                            accept="image/*,application/pdf"
                            // Keep the input associated with the drop-zone label without removing
                            // it from the interactive tree on Android browsers.
                            style={{
                                position: 'absolute',
                                width: 1,
                                height: 1,
                                padding: 0,
                                margin: -1,
                                overflow: 'hidden',
                                clip: 'rect(0 0 0 0)',
                                whiteSpace: 'nowrap',
                                border: 0,
                            }}
                            onChange={(e) => handleFileSelect(e.target.files[0])}
                        />

                        {file ? (
                            <Box>
                                <CloudUploadIcon
                                    sx={{ fontSize: 48, color: 'success.main', mb: 1 }}
                                />
                                <Typography variant="h6" color="success.main">
                                    {file.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Click to change file
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                <CloudUploadIcon
                                    sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
                                />
                                <Typography variant="h6">
                                    Drop an image here or click to browse
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Supports JPG, PNG, GIF, and PDF files
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* PDF Conversion Progress */}
                    {isConverting && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Converting PDF to images... {Math.round(conversionProgress)}%
                            </Typography>
                            <LinearProgress variant="determinate" value={conversionProgress} />
                        </Box>
                    )}

                    {/* Page Review Section */}
                    {isPageReviewPhase && convertedPages.length > 0 && (
                        <PdfPageReview
                            convertedPages={convertedPages}
                            selectedPages={selectedPages}
                            onPageSelectionChange={handlePageSelectionChange}
                            onSelectAllPages={handleSelectAllPages}
                            onPageNameChange={handlePageNameChange}
                            onRemovePage={handleRemovePage}
                        />
                    )}

                    {/* Upload Results */}
                    {uploadResults.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Upload Results
                            </Typography>
                            {uploadResults.map((result, index) => (
                                <Alert
                                    key={index}
                                    severity={result.success ? 'success' : 'error'}
                                    sx={{ mb: 1 }}
                                >
                                    Page {result.page}:{' '}
                                    {result.success ? 'Uploaded successfully' : result.error}
                                </Alert>
                            ))}
                        </Box>
                    )}

                    {/* Error Display */}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading || isConverting}>
                    Cancel
                </Button>

                {isFileSelectPhase && (
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!canSubmitSingleImage}
                        startIcon={
                            uploading ? (
                                <CircularProgress size={20} />
                            ) : isPdfFile(file) ? (
                                <PictureAsPdfIcon />
                            ) : (
                                <CloudUploadIcon />
                            )
                        }
                    >
                        {uploading
                            ? 'Uploading...'
                            : isConverting
                              ? 'Converting PDF...'
                              : isPdfFile(file)
                                ? 'Convert PDF'
                                : 'Upload Floorplan'}
                    </Button>
                )}

                {isPageReviewPhase && (
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!canSubmitBatch}
                        startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    >
                        {uploading
                            ? `Uploading ${selectedPages.length} pages...`
                            : `Upload ${selectedPages.length} Selected Pages`}
                    </Button>
                )}
            </DialogActions>
            {/* Fullscreen loading overlay while uploading */}
            <Backdrop
                open={uploading || uploadPhase === UPLOAD_PHASES.UPLOADING}
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    flexDirection: 'column',
                }}
            >
                <CircularProgress color="inherit" />
                <Typography variant="body1" sx={{ mt: 2 }}>
                    {isPageReviewPhase ? 'Uploading selected pages...' : 'Uploading drawings...'}
                </Typography>
            </Backdrop>
        </Dialog>
    );
}

const Siteplans = () => {
    const [sitePlans, setSitePlans] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState('');
    const [deleting, setDeleting] = useState(false);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [duplicateTarget, setDuplicateTarget] = useState(null);
    const [duplicateName, setDuplicateName] = useState('');
    const [duplicating, setDuplicating] = useState(false);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameTarget, setRenameTarget] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [changeGroupDialogOpen, setChangeGroupDialogOpen] = useState(false);
    const [changeGroupTarget, setChangeGroupTarget] = useState(null);
    const [changeGroupValue, setChangeGroupValue] = useState(null);
    const [changingGroup, setChangingGroup] = useState(false);
    const [groupingEnabled, setGroupingEnabled] = useState(false);
    const [buildingNames, setBuildingNames] = useState([]);
    const [projectSettings, setProjectSettings] = useState(null);
    const [selectedBuilding, setSelectedBuilding] = useState(null); // null = All Buildings
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const navigate = useNavigate();
    const { projectId } = useParams();
    const { canProject, getProjectRole, isLoading: permissionsLoading } = usePermissions();

    // Permission checks
    const canWrite = canProject(projectId, PERMISSIONS.PROJECT_WRITE);
    const role = getProjectRole(projectId);
    const showExternalSubmissionDeadline =
        isExternalOwnerConsultantRole(role) &&
        projectSettings?.externalSubmissionsAllowed === true &&
        Boolean(projectSettings?.externalSubmissionDeadline);
    const formattedExternalSubmissionDeadline = showExternalSubmissionDeadline
        ? formatDate(projectSettings.externalSubmissionDeadline)
        : null;

    // Define breadcrumb links inside the component where projectId is available
    const breadcrumbLinks = [
        { path: '/projects', label: 'Projects' },
        { path: `/projects/${projectId}`, label: 'Project Dashboard' },
        { path: `/projects/${projectId}/plans`, label: 'Drawings' },
    ];

    // Group floorplans by groupName for grouped view
    const groupedPlans = useMemo(() => {
        if (!groupingEnabled) return null;

        const groups = {};
        sitePlans.forEach((plan) => {
            const group = plan.groupName || null;
            if (!groups[group]) groups[group] = [];
            groups[group].push(plan);
        });

        // Sort groups: named groups alphabetically, then ungrouped last
        const sortedEntries = Object.entries(groups).sort(([a], [b]) => {
            if (a === 'null' || a === null) return 1;
            if (b === 'null' || b === null) return -1;
            return String(a).localeCompare(String(b));
        });

        return sortedEntries;
    }, [sitePlans, groupingEnabled]);

    // Building filter options — derived from actual floorplan data (not project settings)
    // so we only show buildings that have at least one drawing
    const buildingFilterOptions = useMemo(() => {
        if (!groupedPlans) return [];
        return groupedPlans.map(([key]) => key).filter((key) => key !== 'null' && key !== null);
    }, [groupedPlans]);

    const hasUngroupedPlans = useMemo(() => {
        if (!groupedPlans) return false;
        return groupedPlans.some(([key]) => key === 'null' || key === null);
    }, [groupedPlans]);

    // Load floorplans and project settings on component mount
    const loadFloorplans = useCallback(async () => {
        if (!projectId) {
            setError('No project ID provided');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const [plans, projectData] = await Promise.all([
                floorplanService.getFloorplansByProject(projectId),
                fetchProjectById(projectId),
            ]);

            setSitePlans(plans);
            setGroupingEnabled(projectData.floorplanGroupingEnabled || false);
            setBuildingNames(projectData.buildingNames || []);
            setProjectSettings({
                externalSubmissionsAllowed: projectData.externalSubmissionsAllowed,
                externalSubmissionDeadline: projectData.externalSubmissionDeadline,
            });
        } catch (err) {
            console.error('Error loading floorplans:', err);
            setError(err.message || 'Failed to load floorplans');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        loadFloorplans();
    }, [loadFloorplans]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handlePlanClick = useCallback(
        (plan) => {
            // Navigate to viewplan with the correct URL structure: /projects/:projectId/plans/:floorplanId
            navigate(`/projects/${projectId}/plans/${plan.id}`, {
                state: { selectedPlan: plan },
            });
        },
        [projectId, navigate]
    );

    const handleUploadSuccess = useCallback(() => {
        // Refresh the floorplans list
        loadFloorplans();
    }, [loadFloorplans]);

    const handleRequestDelete = useCallback((plan) => {
        setDeleteTarget(plan);
        setDeleteConfirm('');
        setDeleteDialogOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setError(null);
        try {
            await floorplanService.deleteFloorplan(deleteTarget.id);
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
            setDeleteConfirm('');
            await loadFloorplans();
        } catch (err) {
            setError(err.message || 'Failed to delete floorplan');
        } finally {
            setDeleting(false);
        }
    }, [deleteTarget, loadFloorplans]);

    const handleRequestDuplicate = useCallback((plan) => {
        setDuplicateTarget(plan);
        setDuplicateName(`${plan.fileName} (Copy)`);
        setDuplicateDialogOpen(true);
    }, []);

    const handleRequestRename = useCallback((plan) => {
        setRenameTarget(plan);
        setRenameValue(plan.fileName || '');
        setRenameDialogOpen(true);
    }, []);

    const handleConfirmRename = useCallback(async () => {
        if (!renameTarget) return;
        const trimmedName = renameValue.trim();
        if (!trimmedName || trimmedName === renameTarget.fileName) {
            setRenameDialogOpen(false);
            setRenameTarget(null);
            setRenameValue('');
            return;
        }
        setRenaming(true);
        setError(null);
        try {
            // Preserve the existing group assignment; only the name changes.
            const updated = await floorplanService.updateFloorplan(renameTarget.id, {
                name: trimmedName,
                groupName: renameTarget.groupName || null,
            });
            setSitePlans((prev) =>
                prev.map((p) => (p.id === updated.id ? { ...p, fileName: updated.fileName } : p))
            );
            setRenameDialogOpen(false);
            setRenameTarget(null);
            setRenameValue('');
        } catch (err) {
            setError(err.message || 'Failed to rename drawing');
        } finally {
            setRenaming(false);
        }
    }, [renameTarget, renameValue]);

    const handleRequestChangeGroup = useCallback((plan) => {
        setChangeGroupTarget(plan);
        setChangeGroupValue(plan.groupName || null);
        setChangeGroupDialogOpen(true);
    }, []);

    const handleConfirmChangeGroup = useCallback(async () => {
        if (!changeGroupTarget) return;
        setChangingGroup(true);
        setError(null);
        try {
            const updated = await floorplanService.updateFloorplan(changeGroupTarget.id, {
                name: changeGroupTarget.fileName,
                groupName: changeGroupValue || null,
            });
            setSitePlans((prev) =>
                prev.map((p) => (p.id === updated.id ? { ...p, groupName: updated.groupName } : p))
            );
            setChangeGroupDialogOpen(false);
            setChangeGroupTarget(null);
            setChangeGroupValue(null);
        } catch (err) {
            setError(err.message || 'Failed to change group');
        } finally {
            setChangingGroup(false);
        }
    }, [changeGroupTarget, changeGroupValue]);

    const handleToggleGroupCollapse = useCallback((groupKey) => {
        setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
    }, []);

    const handleConfirmDuplicate = useCallback(async () => {
        if (!duplicateTarget || !duplicateName.trim()) return;
        setDuplicating(true);
        setError(null);
        try {
            await floorplanService.duplicateFloorplan(duplicateTarget.id, duplicateName.trim());
            setDuplicateDialogOpen(false);
            setDuplicateTarget(null);
            setDuplicateName('');
            await loadFloorplans();
        } catch (err) {
            setError(err.message || 'Failed to duplicate floorplan');
        } finally {
            setDuplicating(false);
        }
    }, [duplicateTarget, duplicateName, loadFloorplans]);

    // Show loading state
    if (loading) {
        return (
            <Box
                sx={{
                    backgroundColor: theme.palette.neutral.white,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                }}
            >
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Loading floorplans...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ backgroundColor: theme.palette.neutral.white, position: 'relative' }}>
            <Box
                sx={{
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Breadcrumbs links={breadcrumbLinks} />
                {formattedExternalSubmissionDeadline && (
                    <Chip
                        icon={<EventAvailableIcon />}
                        label={`Deficiencies due ${formattedExternalSubmissionDeadline}`}
                        size="small"
                        variant="outlined"
                        sx={{
                            borderRadius: '6px',
                            color: 'text.secondary',
                            borderColor: 'divider',
                            backgroundColor: 'background.paper',
                            '& .MuiChip-icon': {
                                color: 'text.secondary',
                            },
                        }}
                    />
                )}
            </Box>

            {/* Show error if there's one */}
            {error && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                    {error}
                </Alert>
            )}

            {/* Search + Building filter toolbar */}
            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    placeholder={
                        selectedBuilding
                            ? selectedBuilding === '__ungrouped'
                                ? 'Search ungrouped drawings...'
                                : `Search in ${selectedBuilding}...`
                            : 'Search drawings...'
                    }
                    value={searchTerm}
                    onChange={handleSearchChange}
                    size="small"
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 2,
                        '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                                borderColor: theme.palette.primary.main,
                            },
                        },
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: theme.palette.grey[500] }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {/* Building filter chips — only when grouping is enabled and there are buildings */}
                {groupingEnabled && buildingFilterOptions.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.75,
                            mt: 1,
                            alignItems: 'center',
                        }}
                    >
                        <Chip
                            label="All"
                            size="small"
                            variant={selectedBuilding === null ? 'filled' : 'outlined'}
                            color={selectedBuilding === null ? 'primary' : 'default'}
                            onClick={() => setSelectedBuilding(null)}
                            sx={{
                                fontWeight: selectedBuilding === null ? 600 : 400,
                                borderRadius: '6px',
                            }}
                        />
                        {buildingFilterOptions.map((name) => (
                            <Chip
                                key={name}
                                label={name}
                                size="small"
                                variant={selectedBuilding === name ? 'filled' : 'outlined'}
                                color={selectedBuilding === name ? 'primary' : 'default'}
                                onClick={() =>
                                    setSelectedBuilding(selectedBuilding === name ? null : name)
                                }
                                sx={{
                                    fontWeight: selectedBuilding === name ? 600 : 400,
                                    borderRadius: '6px',
                                }}
                            />
                        ))}
                        {hasUngroupedPlans && (
                            <Chip
                                label="Ungrouped"
                                size="small"
                                variant={selectedBuilding === '__ungrouped' ? 'filled' : 'outlined'}
                                color={selectedBuilding === '__ungrouped' ? 'default' : 'default'}
                                onClick={() =>
                                    setSelectedBuilding(
                                        selectedBuilding === '__ungrouped' ? null : '__ungrouped'
                                    )
                                }
                                sx={{
                                    fontWeight: selectedBuilding === '__ungrouped' ? 600 : 400,
                                    fontStyle: 'italic',
                                    borderRadius: '6px',
                                }}
                            />
                        )}
                    </Box>
                )}
            </Box>

            {/* Show message if no plans found */}
            {!loading && sitePlans.length === 0 && !error && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="h6" color="textSecondary">
                        No floorplans found for this project
                    </Typography>
                    {permissionsLoading ? (
                        <Skeleton
                            variant="rectangular"
                            width={200}
                            height={36}
                            sx={{ borderRadius: 1, mt: 2, mx: 'auto' }}
                        />
                    ) : (
                        canWrite && (
                            <>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    Upload floorplans to get started
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setUploadModalOpen(true)}
                                    sx={{ mt: 2 }}
                                >
                                    Upload First Floorplan
                                </Button>
                            </>
                        )
                    )}
                </Box>
            )}

            {/* Display floorplans - grouped or flat */}
            {groupingEnabled && groupedPlans ? (
                // Grouped view
                (() => {
                    let anyVisible = false;

                    const sections = groupedPlans.map(([groupKey, plans]) => {
                        const isUngrouped = groupKey === 'null' || groupKey === null;
                        const displayName = isUngrouped ? 'Ungrouped' : groupKey;

                        // Building filter: skip sections that don't match
                        if (selectedBuilding !== null) {
                            if (selectedBuilding === '__ungrouped' && !isUngrouped) return null;
                            if (selectedBuilding !== '__ungrouped' && groupKey !== selectedBuilding)
                                return null;
                        }

                        // Search filter: check if any plan matches
                        const term = searchTerm.toLowerCase();
                        const visiblePlans = plans.filter(
                            (plan) =>
                                !searchTerm.trim() ||
                                plan.fileName.toLowerCase().includes(term) ||
                                (plan.groupName && plan.groupName.toLowerCase().includes(term))
                        );

                        if (visiblePlans.length === 0) return null;
                        anyVisible = true;

                        const isCollapsed = collapsedGroups[groupKey] || false;
                        const visibleCount = visiblePlans.length;

                        return (
                            <Box key={groupKey} sx={{ mb: 3 }}>
                                {/* Building section header */}
                                <Box
                                    onClick={() => handleToggleGroupCollapse(groupKey)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        mb: 2,
                                        pb: 1,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        userSelect: 'none',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 600,
                                                color: isUngrouped
                                                    ? theme.palette.grey[500]
                                                    : theme.palette.text.primary,
                                                fontStyle: isUngrouped ? 'italic' : 'normal',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {displayName}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: theme.palette.grey[500],
                                                lineHeight: 1,
                                                ml: 1,
                                                fontSize: '0.8125rem',
                                            }}
                                        >
                                            · {visibleCount}{' '}
                                            {visibleCount === 1 ? 'drawing' : 'drawings'}
                                        </Typography>
                                    </Box>

                                    {isCollapsed ? (
                                        <ExpandMore
                                            sx={{
                                                fontSize: 20,
                                                color: theme.palette.text.secondary,
                                            }}
                                        />
                                    ) : (
                                        <ExpandLess
                                            sx={{
                                                fontSize: 20,
                                                color: theme.palette.text.secondary,
                                            }}
                                        />
                                    )}
                                </Box>

                                <Collapse in={!isCollapsed}>
                                    <Grid container spacing={2}>
                                        {plans.map((plan) => {
                                            const isVisible = visiblePlans.includes(plan);
                                            return (
                                                <FloorplanCard
                                                    key={plan.id}
                                                    plan={plan}
                                                    onClick={handlePlanClick}
                                                    onDelete={handleRequestDelete}
                                                    onDuplicate={handleRequestDuplicate}
                                                    onRename={handleRequestRename}
                                                    onChangeGroup={handleRequestChangeGroup}
                                                    isVisible={isVisible}
                                                    canWrite={!permissionsLoading && canWrite}
                                                    showGrouping={groupingEnabled}
                                                />
                                            );
                                        })}
                                    </Grid>
                                </Collapse>
                            </Box>
                        );
                    });

                    // Show "no results" message when filters/search yield nothing
                    if (!anyVisible && (searchTerm.trim() || selectedBuilding !== null)) {
                        return (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <Typography variant="h6" color="textSecondary">
                                    No drawings match your{' '}
                                    {searchTerm.trim() && selectedBuilding !== null
                                        ? 'search and filter'
                                        : searchTerm.trim()
                                          ? 'search'
                                          : 'filter'}
                                </Typography>
                            </Box>
                        );
                    }

                    return sections;
                })()
            ) : (
                // Flat view (default / grouping disabled)
                <Grid container spacing={2}>
                    {sitePlans.map((plan) => {
                        const isVisible =
                            !searchTerm.trim() ||
                            plan.fileName.toLowerCase().includes(searchTerm.toLowerCase());

                        return (
                            <FloorplanCard
                                key={plan.id}
                                plan={plan}
                                onClick={handlePlanClick}
                                onDelete={handleRequestDelete}
                                onDuplicate={handleRequestDuplicate}
                                onRename={handleRequestRename}
                                isVisible={isVisible}
                                canWrite={!permissionsLoading && canWrite}
                                showGrouping={false}
                            />
                        );
                    })}
                </Grid>
            )}

            {/* Floating Action Button for Upload */}
            {sitePlans.length > 0 &&
                (permissionsLoading ? (
                    <Skeleton
                        variant="circular"
                        width={56}
                        height={56}
                        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}
                    />
                ) : (
                    canWrite && (
                        <Fab
                            color="primary"
                            sx={{
                                position: 'fixed',
                                bottom: 24,
                                right: 24,
                                zIndex: 1000,
                            }}
                            onClick={() => setUploadModalOpen(true)}
                        >
                            <AddIcon />
                        </Fab>
                    )
                ))}

            {/* Upload Modal */}
            <FloorplanUploadModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                onUpload={handleUploadSuccess}
                projectId={projectId}
                groupingEnabled={groupingEnabled}
                buildingNames={buildingNames}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Delete Drawing
                    <IconButton
                        onClick={() => setDeleteDialogOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This action is permanent. Deleting this drawing will also delete all pins,
                        deficiencies, rooms, images, and notifications tied to it.
                    </Alert>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        To confirm deletion of &quot;{deleteTarget?.fileName}&quot;, type DELETE
                        below.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Type DELETE to confirm"
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleConfirmDelete}
                        disabled={deleting || deleteConfirm.trim() !== 'DELETE'}
                        startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Duplicate Floorplan Dialog */}
            <Dialog
                open={duplicateDialogOpen}
                onClose={() => setDuplicateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Duplicate Drawing
                    <IconButton
                        onClick={() => setDuplicateDialogOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This will create an independent copy of &quot;{duplicateTarget?.fileName}
                        &quot; including all its rooms.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="New Floorplan Name"
                        value={duplicateName}
                        onChange={(e) => setDuplicateName(e.target.value)}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDuplicateDialogOpen(false)} disabled={duplicating}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDuplicate}
                        disabled={duplicating || !duplicateName.trim()}
                        startIcon={
                            duplicating ? <CircularProgress size={20} /> : <ContentCopyIcon />
                        }
                    >
                        {duplicating ? 'Duplicating...' : 'Duplicate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rename Drawing Dialog */}
            <Dialog
                open={renameDialogOpen}
                onClose={() => setRenameDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Rename Drawing
                    <IconButton
                        onClick={() => setRenameDialogOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Renaming only changes the drawing&apos;s name. Its pins, rooms,
                        deficiencies, and images are preserved.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Drawing Name"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && renameValue.trim() && !renaming) {
                                handleConfirmRename();
                            }
                        }}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameDialogOpen(false)} disabled={renaming}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmRename}
                        disabled={
                            renaming ||
                            !renameValue.trim() ||
                            renameValue.trim() === renameTarget?.fileName
                        }
                        startIcon={
                            renaming ? (
                                <CircularProgress size={20} />
                            ) : (
                                <DriveFileRenameOutlineIcon />
                            )
                        }
                    >
                        {renaming ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Change Building Dialog */}
            {groupingEnabled && (
                <Dialog
                    open={changeGroupDialogOpen}
                    onClose={() => setChangeGroupDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        Change Building
                        <IconButton
                            onClick={() => setChangeGroupDialogOpen(false)}
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Assign &quot;{changeGroupTarget?.fileName}&quot; to a building.
                        </Typography>
                        <FormControl fullWidth margin="normal" size="small">
                            <InputLabel>Building</InputLabel>
                            <Select
                                value={changeGroupValue || ''}
                                onChange={(e) => setChangeGroupValue(e.target.value || null)}
                                label="Building"
                            >
                                <MenuItem value="">
                                    <em>None (Ungrouped)</em>
                                </MenuItem>
                                {buildingNames.map((name) => (
                                    <MenuItem key={name} value={name}>
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setChangeGroupDialogOpen(false)}
                            disabled={changingGroup}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmChangeGroup}
                            disabled={changingGroup}
                            startIcon={
                                changingGroup ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <DriveFileMoveIcon />
                                )
                            }
                        >
                            {changingGroup ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default Siteplans;
